import { ReactNode, useCallback, useId, useRef } from 'react';

type TabsProps = {
  tabs: string[];
  value: number;
  onChange: (index: number) => void;
  /**
   * Shared id base linking each tab to its panel. Generate it with
   * `useTabsId()` and pass the same value to every `TabPanel`. Without it the
   * `aria-controls` ids would point at elements that do not exist, which is
   * worse than omitting them.
   */
  idBase?: string;
  className?: string;
};

/** Id base shared by a `Tabs` and its `TabPanel`s. */
export const useTabsId = () => useId();

/**
 * Tab bar with the full APG pattern.
 *
 * The production `Tabs` has `role="tablist"` and `aria-selected` but stops
 * there: no `aria-controls`, no `role="tabpanel"`, and no arrow-key
 * navigation, so a screen-reader user cannot tell which panel a tab drives and
 * a keyboard user must Tab through every tab to reach the content.
 *
 * This adds roving tabindex (only the active tab is in the tab order),
 * Left/Right/Home/End handling, and the `aria-controls` ⇄ `aria-labelledby`
 * pairing with `TabPanel`.
 */
export const Tabs = ({
  tabs,
  value,
  onChange,
  idBase,
  className = '',
}: TabsProps) => {
  const autoId = useId();
  const baseId = idBase ?? autoId;
  const listRef = useRef<HTMLDivElement>(null);

  const focusTab = useCallback((index: number) => {
    const el = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[
      index
    ];
    el?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      let next: number | null = null;
      if (e.key === 'ArrowRight') next = (value + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') next = (value - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;

      if (next !== null) {
        e.preventDefault();
        onChange(next);
        focusTab(next);
      }
    },
    [value, tabs.length, onChange, focusTab],
  );

  return (
    <div
      ref={listRef}
      role="tablist"
      className={`ds-tabs ${className}`}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab, i) => (
        <button
          key={tab}
          type="button"
          role="tab"
          id={`${baseId}-tab-${i}`}
          aria-selected={i === value}
          aria-controls={`${baseId}-panel-${i}`}
          tabIndex={i === value ? 0 : -1}
          className="ds-tab"
          onClick={() => onChange(i)}
        >
          {tab}
        </button>
      ))}
    </div>
  );
};

type TabPanelProps = {
  /** Must match the `baseId` of the owning Tabs — see `useTabsIds`. */
  baseId: string;
  index: number;
  active: boolean;
  children: ReactNode;
};

export const TabPanel = ({ baseId, index, active, children }: TabPanelProps) =>
  active ? (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${index}`}
      aria-labelledby={`${baseId}-tab-${index}`}
      tabIndex={0}
    >
      {children}
    </div>
  ) : null;

type SegmentedProps<T extends string> = {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
  /** Names the group for screen readers, e.g. "Table density". */
  label: string;
  className?: string;
};

/**
 * Segmented control — for switching a *view* (density, date range), as opposed
 * to `Tabs`, which switches a *section*. Using the right one of the two is a
 * semantic choice, not a visual one: this is a radio group, not a tablist.
 */
export const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  label,
  className = '',
}: SegmentedProps<T>) => (
  <div className={`ds-segmented ${className}`} role="group" aria-label={label}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        className="ds-segment"
        aria-pressed={o.value === value}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);
